// src/autodesk_usage/api/customerDetail.ts
// All comments in English only
import { http } from "@/core/http";

// ==== Types (aligned with OpenAPI) ====
export type RiskLevel = "High" | "Medium" | "Low";

export interface Pagination {
  page: number;
  page_size: number;
  total: number;
}

export interface CustomerSummaryResponse {
  company_name: string;
  as_of_month: string;
  risk_score_final: number;
  risk_level: RiskLevel;
  risk_label_primary?: string | null;
  behavior_label_company?: string | null;
  churn_probability_company: number; // 0..1
  renewal_due_ratio: number; // 0..1
  renewed_ratio: number; // 0..1
  expired_ratio: number; // 0..1
  top_risk_products: {
    product: string;
    risk_score_final: number;
    risk_level: RiskLevel;
    behavior_label?: string | null;
  }[];
  extra_metrics?: { key: string; value: number }[];

  // --- Optional: LLM summary fields for company-level narrative ---
  llm_summary?: string | null; // free-form narrative for UI
  llm_key_factors?: string[]; // bullet points of key drivers
  llm_actions?: string[]; // recommended actions list
}

export interface ProductItem {
  product: string;
  risk_score_final: number;
  risk_level: RiskLevel;
  risk_label?: string | null;
  ur_cap?: number | null; // 0..1
  slope_4m?: number | null;
  overuse_index?: number | null;
  renewal_status?: string | null;
  growth_opportunity_flag?: boolean | null;
}

export interface ProductsListResponse {
  company_name: string;
  as_of_month: string;
  pagination: Pagination;
  items: ProductItem[];
}

export interface ProductUsagePoint {
  usage_month: string; // YYYY-MM-01
  used_users: number;
  active_seats: number;
  overuse_index?: number | null;
}

export interface ProductTrendResponse {
  company_name: string;
  product: string;
  series: ProductUsagePoint[];
}

export interface ProductRiskPoint {
  usage_month: string; // date (YYYY-MM-01)
  ur_cap?: number | null;
  ur_ewma_12m?: number | null;
  slope_4m?: number | null;
  risk_score_final?: number | null;
  risk_level?: RiskLevel | null;
}

export interface ProductRiskTrendResponse {
  company_name: string;
  product: string;
  series: ProductRiskPoint[];
}

// LLM analysis for a specific customer + product
export interface ProductLLMAnalysisResponse {
  company_name: string;
  product: string;
  as_of_month: string; // "YYYY-MM"

  // Main narrative fields
  // May be null when there is no analysis available
  analysis_text: string | null;

  // Optional serialized actions (JSON string) or free-form text
  actions_text?: string | null;

  // Structured risk reason flags returned as a JSON object
  // Example: { "overuse": true, "usage_down": true, ... }
  risk_reason_summary?:
    | {
        [key: string]: boolean | null;
      }
    | null;

  // Explain codes can be a simple string array (latest API)
  // or an object of code -> description (to stay backward compatible)
  explain_codes?:
    | string[]
    | {
        [key: string]: string | null;
      }
    | null;

  // Model metadata
  llm_model_name?: string | null;
  llm_generated_at?: string | null; // ISO datetime string
  data_version?: string | null;

  // Prompt context used to generate this result
  llm_prompt?:
    | {
        raw?: string;
        // Additional prompt-related fields are allowed
        [key: string]: unknown;
      }
    | null;

  // Original raw LLM response (JSON)
  llm_raw_output?:
    | {
        overall_comment?: string;
        crm_recommendation?: unknown;
        // Additional model-specific fields are allowed
        [key: string]: unknown;
      }
    | null;
}

export interface CompanyRiskHistoryResponse {
  company_name: string;
  series: {
    usage_month: string;
    risk_score_final: number;
    churn_probability_company: number;
    behavior_label_company?: string | null;
    risk_level?: RiskLevel | null;
  }[];
}

// ==== API functions (via core/http) ====
// Using URLSearchParams to keep the same pattern as dashboard.ts

export function getCustomerSummary(company_name: string, as_of_month?: string) {
  const params = new URLSearchParams();
  if (as_of_month) params.set("as_of_month", as_of_month);
  return http.get<CustomerSummaryResponse>(
    `/autodesk_usage/customers/${encodeURIComponent(
      company_name
    )}/summary?${params.toString()}`
  );
}

export function getCustomerProducts(
  company_name: string,
  as_of_month?: string,
  page = 1,
  page_size = 50,
  sort = "-risk_score_final"
) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
    sort,
  });
  if (as_of_month) params.set("as_of_month", as_of_month);
  return http.get<ProductsListResponse>(
    `/autodesk_usage/customers/${encodeURIComponent(
      company_name
    )}/products?${params.toString()}`
  );
}

export function getCompanyRiskHistory(
  company_name: string,
  months = 12,
  as_of_month?: string
) {
  const params = new URLSearchParams({ months: String(months) });
  if (as_of_month) params.set("as_of_month", as_of_month);
  return http.get<CompanyRiskHistoryResponse>(
    `/autodesk_usage/customers/${encodeURIComponent(
      company_name
    )}/risk-history?${params.toString()}`
  );
}

export function getProductTrend(
  company_name: string,
  product: string,
  months = 12,
  as_of_month?: string
) {
  const params = new URLSearchParams({ months: String(months) });
  if (as_of_month) params.set("as_of_month", as_of_month);
  return http.get<ProductTrendResponse>(
    `/autodesk_usage/customers/${encodeURIComponent(
      company_name
    )}/products/${encodeURIComponent(product)}/trend?${params.toString()}`
  );
}

export function getProductRiskTrend(
  company_name: string,
  product: string,
  months = 12,
  as_of_month?: string
) {
  const params = new URLSearchParams({ months: String(months) });
  if (as_of_month) params.set("as_of_month", as_of_month);
  return http.get<ProductRiskTrendResponse>(
    `/autodesk_usage/customers/${encodeURIComponent(
      company_name
    )}/products/${encodeURIComponent(product)}/risk-trend?${params.toString()}`
  );
}

export function getProductLLMAnalysis(
  company_name: string,
  product: string,
  as_of_month?: string
) {
  const params = new URLSearchParams();
  if (as_of_month) params.set("as_of_month", as_of_month);

  const query = params.toString();
  const basePath = `/autodesk_usage/customers/${encodeURIComponent(
    company_name
  )}/products/${encodeURIComponent(product)}/llm-analysis`;
  const url = query ? `${basePath}?${query}` : basePath;

  return http.get<ProductLLMAnalysisResponse>(url);
}
